import NumberFlow from "@number-flow/react";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { TooltipIcon } from "@solomon-labs/design/components/icons";
import {
    ButtonIcon,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import React from "react";

// Bundled copy (data-URL'd by tsup) so the lifted card renders the coin
// with no `/img/` runtime path — works vendored or package-installed by
// an external (docs/wiki) consumer. The guilloche backdrop is NOT bundled:
// it's a mobile-only decorative wash that the sim/external surfaces don't
// want, and apps/main supplies its own via the `guillocheSrc` prop.
import usdvCoin from "../../assets/yaas/usdv-coin-3d.png";
import { GuillochePattern } from "../../components/Brand/GuillochePattern";
import type { YaasWalletStats } from "../../types/yaas";
import { formatAdvertisedApy } from "../../utils/apy";

import { PendingEarningsTicker } from "./PendingEarningsTicker";
import type { DistributionMode } from "./types";
import { YaasNextDistribution } from "./YaasNextDistribution";

export interface PortfolioSummaryProps {
    /** Live claims remain accessible for historical Claim rewards after switching to Stream. */
    claimContent?: React.ReactNode;
    /** Body of the tooltip beside the Pending figure. Defaults to the
     *  accrual explanation; a caller whose position cannot accrue passes
     *  the reason instead, so the tooltip never explains earnings to a
     *  wallet that is not earning. */
    pendingTooltip?: React.ReactNode;
    walletStats: YaasWalletStats | null;
    averageAPY: string | null;
    /** Personal lifetime paid rate, in percent, for completed settlements. */
    historicalApr?: string | null;
    isLoading: boolean;
    hasActivePosition: boolean;
    /** User's committed distribution mode. Drives whether the Claim
     *  Rewards button renders (claim-only — stream-mode users don't
     *  see it since rewards auto-route). */
    distributionMode: DistributionMode;
    /** Show the "Stream Mode" / "Claim Mode" label beside Pending. Off
     *  while the Stream / Claim choice is flagged off: with no choice to
     *  make, naming the mode is noise. Defaults to `true`. */
    showDistributionMode?: boolean;
    /** ISO timestamp of the next distribution cycle. Drives the
     *  inline `YaasNextDistribution` countdown rendered between the
     *  hero number and the Pending row. */
    nextDistributionDate?: string | null;
    /** Projected qualifying-distribution date when the user is
     *  accruing below the per-distribution minimum. Wins over
     *  `nextDistributionDate` when present so the countdown reflects
     *  the user's real wait. */
    projectedNextDistributionDate?: string | null;
    /** ISO start of the period that pays at `nextDistributionDate`. The
     *  countdown bar fills between the two. Omit for the seven-day cycle
     *  the sims are built on. */
    distributionPeriodStartsAt?: string | null;
    /**
     * Pending-earnings ticker — DI'd so this card stays SDK-free.
     *
     *   - apps/main injects its `RetailPendingEarningsTicker` (which
     *     calls `useDistributions` + computes the live accrual). The
     *     shim path passes it here so apps/main behavior is unchanged.
     *   - sim / external consumers omit it; the card falls back to the
     *     presentational {@link PendingEarningsTicker} driven by the
     *     `pendingValue` prop (the sim driver ticks that up on a timer).
     */
    pendingTicker?: React.ReactNode;
    /** Live pending value fed to the fallback presentational ticker
     *  when `pendingTicker` is omitted (sim path). Ignored when a
     *  `pendingTicker` node is supplied. */
    pendingValue?: number;
    /** Source for the mobile-only hero guilloche backdrop. apps/main
     *  passes its served `/img/guilloche-hero.svg`; sim / external
     *  consumers omit it — the card renders just the center (coin +
     *  earnings), no decorative wash. */
    guillocheSrc?: string;
    /** When true, the "You've earned" hero number animates via
     *  NumberFlow so a value change count-ups smoothly (the sim seeds a
     *  low value then bumps to the target on mount). apps/main leaves
     *  this off — the real total renders statically as before. */
    animateEarnedCountUp?: boolean;
}

/**
 * YaaS live-earnings card — the active-position left-column hero.
 * Embossed USDv coin medallion, "You've earned ({apy} APY)" eyebrow, a
 * large cumulative-earned hero number, the "Next Distribution"
 * countdown + progress bar, and a green live "Pending" ticker + mode
 * label. Lifted from apps/main (`pages/Yaas/sections/PortfolioSummary`)
 * so external sim consumers can render it.
 *
 * Mechanism-only / SDK-free: the pending ticker is DI'd (apps/main
 * injects the real `useDistributions`-backed ticker; the sim driver
 * feeds a mock `pendingValue`). Assets are bundled imports, not
 * `/img/` runtime paths.
 */
export function PortfolioSummary({
    claimContent,
    pendingTooltip,
    walletStats,
    averageAPY,
    historicalApr = null,
    isLoading,
    hasActivePosition,
    distributionMode,
    showDistributionMode = true,
    nextDistributionDate,
    projectedNextDistributionDate,
    distributionPeriodStartsAt,
    pendingTicker,
    pendingValue = 0,
    guillocheSrc,
    animateEarnedCountUp = false
}: PortfolioSummaryProps): React.ReactElement {
    const formattedApy = formatAdvertisedApy(averageAPY);
    const numFmt = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    const stats = walletStats ?? {
        totalPositions: 0,
        formattedTotalPositions: "$0.00",
        totalYieldEarned: 0,
        formattedTotalYieldEarned: "$0.00",
        totalTvl: 0,
        formattedTvl: "$0"
    };

    return (
        // Mirrors HeroSection's outer flex structure (gap-0 items-center)
        // so the active state composes inside USDvLandingPanel with the
        // same vertical rhythm as the landing — coin sits above the
        // hero block, subtitle below, both separated by md:mt-4 gaps.
        <div className="relative flex flex-col gap-0 items-center justify-center md:justify-start text-center w-full overflow-hidden md:overflow-visible min-h-[300px] md:min-h-0 border-b border-line-base md:border-b-0">
            {/* Mobile-only hero guilloche backdrop — mirrors HeroSection's
                mobile treatment so the active/pending states share the
                same edge-to-edge framed hero. Suppressed on desktop where
                the surrounding USDvLandingPanel supplies its own
                panel-wide guilloche. */}
            {guillocheSrc && (
                <div className="md:hidden absolute inset-0 pointer-events-none">
                    <GuillochePattern
                        src={guillocheSrc}
                        scale={3.24}
                        animateIn
                    />
                </div>
            )}

            {/* USDv coin — desktop only. On mobile the hero shows the
                portfolio-summary content directly on the guilloche
                backdrop without the coin. */}
            <div className="hidden md:flex relative size-[180px] items-center justify-center">
                <img
                    src={usdvCoin}
                    alt="USDv"
                    className="size-full relative z-10"
                />
            </div>
            {/* Earned amount and the available reward rate. */}
            <div className="relative z-10 flex flex-col gap-2 items-center md:mt-4">
                <Text
                    variant="small"
                    render={<span />}
                    className="text-foreground-neutral"
                >
                    You&apos;ve earned
                    {historicalApr !== null ? (
                        <>
                            {` (${new BigDecimal(historicalApr).round(1).toString()}% APY)`}
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger
                                        aria-label="About historical APY"
                                        tabIndex={0}
                                        render={
                                            <span className="inline-flex align-middle ml-1 cursor-help" />
                                        }
                                    >
                                        <TooltipIcon className="size-3.5" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        Your historical annualized rate, based
                                        on rewards paid and your qualifying
                                        balance over time since you started
                                        earning. Rewards are not compounded.
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </>
                    ) : averageAPY ? (
                        ` (${formattedApy} APY)`
                    ) : (
                        ""
                    )}
                </Text>
                <Text variant="number-hero" className="text-foreground-base">
                    $
                    {animateEarnedCountUp ? (
                        <NumberFlow
                            value={stats.totalYieldEarned}
                            format={{
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            }}
                        />
                    ) : (
                        numFmt.format(stats.totalYieldEarned)
                    )}
                </Text>
            </div>
            {/* Next Distribution + Pending — countdown + progress bar
                followed by the live pending-earnings ticker. */}
            <div className="relative z-10 flex flex-col gap-2 w-full max-w-2xs md:max-w-xs mt-6 text-left">
                <YaasNextDistribution
                    target={
                        projectedNextDistributionDate ??
                        nextDistributionDate ??
                        null
                    }
                    barTarget={nextDistributionDate ?? null}
                    isActive={hasActivePosition}
                    periodStart={distributionPeriodStartsAt}
                />
                {/* Pending row — left cluster reads "Pending: $X" plus
                    the tooltip; right side shows the distribution mode. */}
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1">
                        <Text
                            variant="small"
                            render={<span />}
                            className="text-foreground-muted"
                        >
                            Pending:
                        </Text>
                        {pendingTicker ?? (
                            <PendingEarningsTicker
                                value={pendingValue}
                                isLoaded={!isLoading}
                                variant="number"
                                color="rgb(var(--color-success-base))"
                            />
                        )}
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger
                                    render={
                                        <ButtonIcon
                                            variant="transparent"
                                            size="sm"
                                            aria-label="What is pending earnings?"
                                            icon={<TooltipIcon />}
                                        />
                                    }
                                />
                                <TooltipContent
                                    side="top"
                                    // An injected reason is a short,
                                    // self-contained sentence and reads
                                    // tighter at a narrower cap; the default
                                    // accrual copy is longer and wants the
                                    // wider one. TooltipContent's own
                                    // `maxWidth` scale starts at `xs`
                                    // (20rem) and only goes up, so neither
                                    // cap is reachable through the prop.
                                    className={
                                        pendingTooltip
                                            ? "text-balance max-w-44"
                                            : "text-balance max-w-[14rem]"
                                    }
                                >
                                    {pendingTooltip ?? (
                                        <>
                                            Estimated rewards accrued for the
                                            next distribution.
                                        </>
                                    )}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                    {showDistributionMode && (
                        <Text
                            variant="small"
                            render={<span />}
                            className="text-foreground-muted shrink-0"
                        >
                            {distributionMode === "stream"
                                ? "Stream Mode"
                                : "Claim Mode"}
                        </Text>
                    )}
                </div>
            </div>
            {claimContent}
        </div>
    );
}
